// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { CareCircleMemberCard } from '@/pwa/molecules/CareCircleMemberCard/CareCircleMemberCard';

// mui
import { Button, Stack } from '@mui/material';

export interface CareCircleMember {
  id: string;
  name: string;
  role: string;
  email?: string;
  avatarSrc?: string;
  onRemove?: () => void;
  onResendInvite?: () => void;
}

export interface CareCirclePageProps {
  members: CareCircleMember[];
  recipientFirstName?: string;
  /** True when the logged-in user is the care recipient. */
  isRecipientUser?: boolean;
  onInvite: () => void;
  onBack?: () => void;
}

/**
 * CareCirclePage is the care circle page showing a list of members with
 * actions to invite, edit, and remove.
 */
export const CareCirclePage = ({
  members,
  recipientFirstName,
  isRecipientUser,
  onInvite,
  onBack,
}: CareCirclePageProps) => {
  const title = isRecipientUser
    ? 'Your care circle'
    : recipientFirstName
      ? `${recipientFirstName}'s care circle`
      : 'Care circle';
  const inviteLabel = isRecipientUser
    ? 'Invite to your care circle'
    : recipientFirstName
      ? `Invite to ${recipientFirstName}'s care circle`
      : 'Invite to care circle';

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title={title}
            description='Invite and manage members of this care circle.'
          />
        }
        contentSlot={
          <Stack spacing={3}>
            <Button
              variant='contained'
              size='large'
              fullWidth
              onClick={onInvite}
            >
              {inviteLabel}
            </Button>
            <Stack spacing={2}>
              {members.map(({ id, ...member }) => (
                <CareCircleMemberCard key={id} {...member} />
              ))}
            </Stack>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

CareCirclePage.displayName = 'CareCirclePage';

export default CareCirclePage;
